import { CallKeyExchangeManagerV1 } from "../v1/CallKeyExchangeManagerV1";
import { CallKeyExchangeManagerV2 } from "../v2/CallKeyExchangeManagerV2";

/**
 * Ruolo dell'utente nella connessione realtime.
 */
export type RealtimeRole = "MASTER" | "PEER";

/**
 * Opzioni per la connessione al gateway realtime.
 * @property rtUrl - URL del server delle chiamate (es: https://api.example.com)
 * @property accessToken - API AccessToken per l'autenticazione
 * @property channel - Nome del canale
 * @property role - Ruolo dell'utente (MASTER o PEER)
 * @property liveKitKey - Chiave di cifratura LiveKit (opzionale, usata solo dal MASTER)
 */
export interface RealtimeGatewayOptions {
    url: string;
    token: string;
    channel: string;
    role: RealtimeRole;
    liveKitKey?: Uint8Array; // opzionale, usato solo dal MASTER
}

export interface CallKeyExchangeCompletedInfo {
    liveKitKey: Uint8Array;
    sasCode?: string;
}

/**
 * Interfaccia per il manager dello scambio chiavi nelle chiamate.
 * Definisce i metodi necessari per gestire la connessione, l'autenticazione,
 * lo scambio chiavi e la gestione del canale.
 */
export interface ICallKeyExchangeManager {
    connectAndJoinChannel(requestKeyExchange?: boolean): Promise<boolean>;
    connectOnly(): Promise<void>;
    loginAndJoinChannel(requestKeyExchange?: boolean): Promise<void>;
    requestKeyExchange(timeoutMs?: number): Promise<void>;
    leaveChannel(): void;
    disconnect(): void;
    onKeyExchangeCompleted?: (info: CallKeyExchangeCompletedInfo) => void;
}

//#region Lista versioni CallKeyExchangeManager e relative factory

/**
 * Versioni disponibili di CallKeyExchangeManager.
 * Permette di identificare facilmente quale implementazione specifica utilizzare
 * quando si richiede un'istanza tramite la factory.
 */
export enum CallKeyExchangeManagerVersion {
    V1 = "V1",
    V2 = "V2",
}

/**
* Factory per creare istanze di CallKeyExchangeManager in base alla versione specificata.
* Centralizza la logica di creazione e configurazione delle istanze, rendendo più semplice
* gestire diverse versioni o aggiungere nuove implementazioni in futuro.
*/
const callKeyExchangeManagerFactories: Record<CallKeyExchangeManagerVersion, (options: RealtimeGatewayOptions) => ICallKeyExchangeManager> = {
    [CallKeyExchangeManagerVersion.V1]: (options: RealtimeGatewayOptions) => new CallKeyExchangeManagerV1(options),
    [CallKeyExchangeManagerVersion.V2]: (options: RealtimeGatewayOptions) => new CallKeyExchangeManagerV2(options),
};

/**
 * Factory centralizzata per creare istanze di CallKeyExchangeManager
 * @param version Versione di CallKeyExchangeManager
 * @param options Opzioni per la connessione al gateway realtime
 * @returns Istanza di ICallKeyExchangeManager
 */
export function getCallKeyExchangeManagerInstance(version: CallKeyExchangeManagerVersion, options: RealtimeGatewayOptions)
    : ICallKeyExchangeManager {
    const factory = callKeyExchangeManagerFactories[version];
    if (!factory) throw new Error("CallKeyExchangeManager version not supported");
    return factory(options);
}
// #endregion